import { CartAddEvent } from '@theme/events';

/**
 * Snel toevoegen van een kassakoopje vanuit de verzendbalk (snippets/free-shipping-bar.liquid).
 *
 * De balk zelf is Liquid en staat binnen cart-items-component. Na het toevoegen sturen we
 * hetzelfde cart:update-event als het productformulier, met de opnieuw gerenderde secties.
 * cart-items-component morpht daarmee de lade of winkelwagenpagina (balk inbegrepen) en
 * cart-icon werkt het aantal bij.
 *
 * Eén luisteraar op document, zodat het blijft werken nadat de lade opnieuw is opgebouwd.
 */

const ADD_SELECTOR = '[data-free-shipping-add]';
const SOURCE = 'free-shipping-bar';
const SCROLL_SELECTOR = '[data-free-shipping-scroll]';
const LIST_SELECTOR = '.free-shipping__list';

/**
 * @returns {string[]} De section-id's van alle cart-items-componenten op de pagina.
 */
function getCartSectionIds() {
  const ids = new Set();
  for (const element of document.querySelectorAll('cart-items-component')) {
    if (element instanceof HTMLElement && element.dataset.sectionId) ids.add(element.dataset.sectionId);
  }
  return [...ids];
}

/**
 * @param {Record<string, string> | undefined} sections
 * @returns {Promise<number>} Het aantal artikelen in de winkelwagen.
 */
async function getItemCount(sections) {
  for (const html of Object.values(sections ?? {})) {
    const count = new DOMParser().parseFromString(html, 'text/html').querySelector('[ref="cartItemCount"]')?.textContent;
    if (count) return parseInt(count, 10);
  }

  const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
  const cart = await response.json();
  return cart.item_count;
}

/**
 * @param {HTMLButtonElement} button
 */
async function addUpsell(button) {
  const errorElement = button.closest('[data-free-shipping]')?.querySelector('[data-free-shipping-error]');
  const variantId = button.dataset.variantId;
  const sectionIds = getCartSectionIds();

  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  if (errorElement instanceof HTMLElement) {
    errorElement.textContent = '';
    errorElement.hidden = true;
  }

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });
    const data = await response.json();

    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Toevoegen is niet gelukt. Probeer het opnieuw.');
    }

    const itemCount = await getItemCount(data.sections);

    document.dispatchEvent(
      new CartAddEvent({}, SOURCE, {
        source: SOURCE,
        itemCount,
        variantId,
        sections: data.sections,
      })
    );
  } catch (error) {
    if (errorElement instanceof HTMLElement) {
      errorElement.textContent = error instanceof Error ? error.message : 'Toevoegen is niet gelukt. Probeer het opnieuw.';
      errorElement.hidden = false;
    }
    button.disabled = false;
    button.removeAttribute('aria-busy');
  }
}

/**
 * Zet de pijltjes uit aan het begin of einde van de rij.
 * @param {Element} list
 */
function updateArrows(list) {
  const container = list.closest('[data-free-shipping]');
  if (!container) return;
  const maxScroll = list.scrollWidth - list.clientWidth - 2;
  for (const arrow of container.querySelectorAll(SCROLL_SELECTOR)) {
    if (!(arrow instanceof HTMLButtonElement)) continue;
    const direction = Number(arrow.dataset.freeShippingScroll);
    arrow.disabled = direction < 0 ? list.scrollLeft <= 2 : list.scrollLeft >= maxScroll;
  }
}

/**
 * Schuift de rij kassakoopjes ongeveer één kaartje op (voor muis en trackpad).
 * @param {HTMLButtonElement} arrow
 */
function scrollUpsells(arrow) {
  const list = arrow.closest('[data-free-shipping]')?.querySelector(LIST_SELECTOR);
  if (!(list instanceof HTMLElement)) return;
  const card = list.querySelector('li');
  const step = card ? card.getBoundingClientRect().width + 8 : list.clientWidth * 0.8;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  list.scrollBy({ left: Number(arrow.dataset.freeShippingScroll) * step, behavior: reduceMotion ? 'auto' : 'smooth' });
}

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const arrow = event.target.closest(SCROLL_SELECTOR);
  if (arrow instanceof HTMLButtonElement) {
    scrollUpsells(arrow);
    return;
  }

  const button = event.target.closest(ADD_SELECTOR);
  if (!(button instanceof HTMLButtonElement) || button.disabled) return;

  event.preventDefault();
  addUpsell(button);
});

// De lade wordt bij elke wijziging opnieuw opgebouwd, dus luisteren op document.
document.addEventListener(
  'scroll',
  (event) => {
    if (event.target instanceof Element && event.target.matches(LIST_SELECTOR)) updateArrows(event.target);
  },
  true
);

document.addEventListener('pointerover', (event) => {
  const list = event.target instanceof Element ? event.target.closest('[data-free-shipping]')?.querySelector(LIST_SELECTOR) : null;
  if (list) updateArrows(list);
});
